import {
  Flex,
  Modal,
  Typography,
  Image,
  Button,
  CheckIcon,
} from '@iheartjane/reefer';
import type { Nullish, PriceId } from '@iheartjane/types';
import { useEffect, useMemo, useState } from 'react';

import { EditQuantityButton } from '../ctaButton/editQuantityButton';

import styles from './weightSelectorModal.module.css';

export interface WeightOption {
  count: number;
  isDiscounted?: boolean;
  label: string;
  maxQuantity?: number;
  originalPrice?: Nullish<string>;
  potencyLabel?: Nullish<string>;
  price: string;
  value: PriceId;
}

export interface WeightSelectorModalProps {
  imageUrl?: string;
  isLayered?: boolean;
  isUpdatingCart?: boolean;
  onRequestClose: () => void;
  onSubmit: (selectedQuantities: Record<string, number>) => void;
  open: boolean;
  productName: string;
  weights: WeightOption[];
}

export const WeightSelectorModal = ({
  imageUrl,
  isUpdatingCart,
  isLayered,
  onRequestClose,
  onSubmit,
  open,
  productName,
  weights,
}: WeightSelectorModalProps) => {
  const originalQuantities = useMemo(
    () =>
      weights.reduce(
        (acc, weight) => {
          acc[weight.value] = weight.count;
          return acc;
        },
        {} as Record<string, number>
      ),
    [weights]
  );
  const [selectedQuantities, setSelectedQuantities] =
    useState<Record<string, number>>(originalQuantities);

  useEffect(() => {
    if (open) {
      setSelectedQuantities(originalQuantities);
    }
  }, [open, originalQuantities]);

  const inBag = weights.some((weight) => weight.count > 0);
  const modalLabel = inBag ? 'Edit quantity' : 'Add to bag';

  return (
    <Modal
      contentLabel={modalLabel}
      onRequestClose={onRequestClose}
      open={open}
      overlayClose
      variant="dialogue"
      isLayered={isLayered}
      overlayClassName={isLayered ? styles['layeredOverlay'] : undefined}
    >
      <Modal.Header title={modalLabel} textAlign="center" branded={false} />
      <Modal.Content padding={false}>
        <Flex
          flexDirection="column"
          gap={16}
          className={styles['modalContainer']}
        >
          <Flex alignItems="center" gap={16}>
            <Image
              altText={productName}
              borderRadius="rounded-small"
              src={imageUrl ?? ''}
              height="64px"
              width="64px"
            />
            <Flex className={styles['productCard']} flexDirection="column">
              <Typography color="text-main" variant="body-semibold">
                {productName}
              </Typography>
              {inBag && <Typography color="text-light">In bag</Typography>}
            </Flex>
          </Flex>

          {weights.map((weight) => (
            <Flex
              key={weight.value}
              gap={24}
              px={16}
              py={12}
              background="grays-ultralight"
              borderRadius="sm"
              alignItems="center"
            >
              <Flex flexDirection="column" mr="auto">
                <Typography variant="body-semibold">{weight.label}</Typography>
                {weight.potencyLabel && (
                  <Typography color="text-light">
                    {weight.potencyLabel}
                  </Typography>
                )}
                <Typography color={weight.isDiscounted ? 'info' : 'inherit'}>
                  {weight.price}
                  {weight.originalPrice && (
                    <>
                      {' '}
                      <Typography as="span" color="grays-mid" strikeThrough>
                        {weight.originalPrice}
                      </Typography>
                    </>
                  )}
                </Typography>
              </Flex>
              <EditQuantityButton
                atMaxQuantity={
                  selectedQuantities[weight.value] === weight.maxQuantity
                }
                cartCount={selectedQuantities[weight.value]}
                ctaStyle="modal"
                onDecrease={() => {
                  setSelectedQuantities((prev) => ({
                    ...prev,
                    [weight.value]: prev[weight.value] - 1,
                  }));
                }}
                onIncrease={() => {
                  setSelectedQuantities((prev) => ({
                    ...prev,
                    [weight.value]: prev[weight.value] + 1,
                  }));
                }}
                productName={`${productName} - ${weight.label}`}
                isUpdatingCart={isUpdatingCart}
              />
            </Flex>
          ))}

          <Button
            className={styles['doneButton']}
            disabled={
              Object.keys(selectedQuantities).every(
                (key) => selectedQuantities[key] === originalQuantities[key]
              ) || isUpdatingCart
            }
            full
            startIcon={
              isUpdatingCart && (
                <CheckIcon className={styles['textContrastColor']} />
              )
            }
            label={inBag ? 'Done' : 'Add to bag'}
            onClick={() => onSubmit(selectedQuantities)}
            variant="primary"
          />
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
